/**
 * Shared public-customer Privacy / Measurement preference (Section 19 §21).
 *
 * ONE canonical interpretation of "Help us understand visits" for every
 * Cascade-controlled public customer surface (Hub, offer/landing, booking,
 * claim, SEO). No page implements its own preference logic. Portal /
 * receptionist / owner internal surfaces do NOT consult this file.
 *
 * Semantics
 * ---------
 *   • Default = ON when the visitor has not explicitly opted out AND
 *     durable storage is available (§4).
 *   • OFF = customer explicitly disabled measurement (§7). Existing
 *     `ua_anon_id`, `ua_anon_meta`, `ua_attribution`, `ua_session_id`
 *     are cleared and MUST NOT be recreated while the preference stays
 *     OFF. The preference itself is stored in the narrowest first-party
 *     mechanism available so the customer is not re-asked on every page.
 *   • UNAVAILABLE = the browser is blocking persistent storage. We do
 *     NOT pretend an OFF choice can be durably remembered; the UI shows
 *     a truthful transient state (§18) and the analytics layer degrades
 *     to less attribution without fingerprinting fallback (§10).
 *
 * Anti-circumvention: this file only reads/writes the standard first-party
 * key `ua_privacy_measurement` in localStorage. No fingerprinting, no
 * covert cookies, no IP-based inference. Blocked persistence = reduced
 * attribution, never a reason to bypass the block (§10).
 */

const PREF_KEY = "ua_privacy_measurement"; // "on" | "off"
const CHANGE_EVENT = "ua:privacy-changed";
const KEYS_TO_CLEAR_ON_OPTOUT = ["ua_anon_id", "ua_anon_meta"];
const SESSION_KEYS_TO_CLEAR_ON_OPTOUT = ["ua_attribution", "ua_session_id", "seo_session_id", "seo_attribution"];

// ── storage capability probes (truthful only — never assume) ─────────
export function isLocalStorageAvailable() {
  try {
    const probe = "__ua_probe__";
    window.localStorage.setItem(probe, "1");
    window.localStorage.removeItem(probe);
    return true;
  } catch {
    return false;
  }
}

export function isSessionStorageAvailable() {
  try {
    const probe = "__ua_probe__";
    window.sessionStorage.setItem(probe, "1");
    window.sessionStorage.removeItem(probe);
    return true;
  } catch {
    return false;
  }
}

/**
 * getStoredPreference — reads the raw stored preference or null when
 * either (a) the customer never chose or (b) localStorage is blocked.
 * Callers must not distinguish (a) from (b) at the persistence level;
 * `getEffectiveState()` folds both into the correct outward behavior.
 */
function getStoredPreference() {
  if (!isLocalStorageAvailable()) return null;
  try {
    const v = window.localStorage.getItem(PREF_KEY);
    if (v === "on") return "on";
    if (v === "off") return "off";
    return null;
  } catch {
    return null;
  }
}

/**
 * getEffectiveState — the single source of truth consulted by both the
 * measurement layer and the Privacy Choices UI (§19).
 *
 * Returns:
 *   {
 *     enabled: boolean,        // does measurement operate right now?
 *     durable: boolean,        // can we truthfully persist a choice?
 *     explicit: boolean,       // did the customer explicitly choose?
 *     choice: "on"|"off"|null, // stored choice, or null if not set
 *   }
 */
export function getEffectiveState() {
  const durable = isLocalStorageAvailable();
  const choice = getStoredPreference();
  const explicit = choice === "on" || choice === "off";
  // Default ON when durable storage available AND no explicit off (§4).
  // When durable storage is unavailable, measurement may still run
  // ephemerally per §9 — the toggle just cannot be remembered across
  // pages, which the UI displays truthfully (§18).
  const enabled = choice === "off" ? false : true;
  return { enabled, durable, explicit, choice };
}

/** Convenience: `true` iff measurement is currently permitted to operate. */
export function isMeasurementEnabled() {
  return getEffectiveState().enabled;
}

/**
 * setMeasurementEnabled — persist the customer's explicit choice.
 * When flipped OFF, immediately clears the persistent measurement
 * identifiers so a later customer identification event cannot be joined
 * to the opted-out anonymous journey (§7 rules 1–5, §20).
 *
 * Returns the new effective state (see `getEffectiveState`).
 */
export function setMeasurementEnabled(next) {
  const value = next ? "on" : "off";
  try {
    if (isLocalStorageAvailable()) {
      window.localStorage.setItem(PREF_KEY, value);
    }
  } catch {
    /* durable persistence unavailable — state remains ephemeral, UI reflects that truthfully */
  }
  if (!next) {
    clearMeasurementIdentifiers();
  }
  // Fan-out to listeners (analytics layer + open UI controls).
  try {
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: getEffectiveState() }));
  } catch {
    /* noop */
  }
  return getEffectiveState();
}

/**
 * clearMeasurementIdentifiers — best-effort removal of the persistent
 * anonymous measurement state (§7). Does NOT touch legitimate customer
 * business records (bookings, claims, SMS, calls, appointments) — those
 * are server-owned and remain intact by design.
 */
export function clearMeasurementIdentifiers() {
  try {
    if (isLocalStorageAvailable()) {
      KEYS_TO_CLEAR_ON_OPTOUT.forEach((k) => {
        try { window.localStorage.removeItem(k); } catch { /* noop */ }
      });
    }
  } catch { /* noop */ }
  try {
    if (isSessionStorageAvailable()) {
      SESSION_KEYS_TO_CLEAR_ON_OPTOUT.forEach((k) => {
        try { window.sessionStorage.removeItem(k); } catch { /* noop */ }
      });
    }
  } catch { /* noop */ }
}

/**
 * subscribe — receive notifications when the customer's choice changes,
 * either from the Privacy Choices control on this surface OR another
 * Cascade-controlled surface sharing the same origin (via the `storage`
 * event). Returns an unsubscribe function.
 */
export function subscribePrivacyChanges(handler) {
  if (typeof window === "undefined" || typeof handler !== "function") {
    return () => {};
  }
  const localFn = () => { try { handler(getEffectiveState()); } catch { /* noop */ } };
  const storageFn = (e) => {
    if (!e || e.key !== PREF_KEY) return;
    try { handler(getEffectiveState()); } catch { /* noop */ }
  };
  try { window.addEventListener(CHANGE_EVENT, localFn); } catch { /* noop */ }
  try { window.addEventListener("storage", storageFn); } catch { /* noop */ }
  return () => {
    try { window.removeEventListener(CHANGE_EVENT, localFn); } catch { /* noop */ }
    try { window.removeEventListener("storage", storageFn); } catch { /* noop */ }
  };
}
